import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { NucleicAcidType } from "@/lib/science/nucleic-acid";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { od260ToConcentration, purityRatio } from "../lib/od260";

const numCls =
  "border-black/15 bg-white text-neutral-900 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900";
const KINDS: { value: NucleicAcidType; label: () => string }[] = [
  { value: "dsDNA", label: () => m.od_dsdna() },
  { value: "ssDNA", label: () => m.od_ssdna() },
  { value: "ssRNA", label: () => m.od_ssrna() },
];

export function Od260Calc() {
  const locale = getLocale();
  const [a260, setA260] = useState("1");
  const [dilution, setDilution] = useState("1");
  const [a280, setA280] = useState("");
  const [type, setType] = useState<NucleicAcidType>("dsDNA");

  const out = useMemo(() => {
    const conc = od260ToConcentration(Number(a260), Number(dilution), type);
    const ratio =
      a280.trim() === "" ? null : purityRatio(Number(a260), Number(a280));
    return { conc, ratio };
  }, [a260, dilution, a280, type]);

  const num = (n: number, d = 2) =>
    n.toLocaleString(locale, { maximumFractionDigits: d });

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="od-a260" className="text-muted-foreground">
            {m.od_a260_label()}
          </Label>
          <Input
            id="od-a260"
            type="number"
            value={a260}
            onChange={(e) => setA260(e.target.value)}
            className={numCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="od-dilution" className="text-muted-foreground">
            {m.od_dilution_label()}
          </Label>
          <Input
            id="od-dilution"
            type="number"
            value={dilution}
            onChange={(e) => setDilution(e.target.value)}
            className={numCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="od-a280" className="text-muted-foreground">
            {m.od_a280_label()}
          </Label>
          <Input
            id="od-a280"
            type="number"
            value={a280}
            onChange={(e) => setA280(e.target.value)}
            className={numCls}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            aria-pressed={type === k.value}
            onClick={() => setType(k.value)}
            className={
              type === k.value
                ? "sci-result-zone sci-result rounded-md px-3 py-1.5 text-sm font-semibold"
                : "sci-panel rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            }
          >
            {k.label()}
          </button>
        ))}
      </div>

      {out.conc != null ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.od_result_label()}
            </span>
            <span
              data-testid="od-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {num(out.conc)} <span className="text-xl">{m.od_unit()}</span>
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.od_ngul_label(), value: num(out.conc) },
              {
                label: m.od_ratio_label(),
                value: out.ratio == null ? "—" : num(out.ratio, 2),
              },
            ]}
          />
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.od_hint_empty()}
        </p>
      )}
    </div>
  );
}
